import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { DividerComponent } from '@workbuddy/ui/components/divider';
import { Command } from '@workbuddy/ui/model';
import { ButtonComponent } from '@workbuddy/ui/v2/components/button';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { BulkCommandArgs } from '../../context';
import { boardBulkActionsBridgeStyles, boardBulkActionsStyleId } from './board.bulk.actions.theme';

const actionsCommandId = '__board_bulk_actions_palette__';

@Component({
  selector: 'board-bulk-actions',
  templateUrl: './board.bulk.actions.component.html',
  styleUrls: ['./board.bulk.actions.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ButtonComponent, DividerComponent],
})
export class BoardBulkActionsComponent {
  private readonly document = inject(DOCUMENT);

  public readonly $items = input.required<any[]>({ alias: 'items' });
  public readonly $commands = input.required<Command[]>({ alias: 'commands' });

  public readonly clear = output<void>();
  public readonly command = output<BulkCommandArgs>();

  protected readonly $active = computed(() => this.$items().length > 0);
  protected readonly $actionsCommand = computed(() => new Command({ _id: actionsCommandId, label: 'Actions', iconLeft: 'fa-regular fa-command' }));

  /**
   * Commands that fire immediately (no palette settings panel) — e.g. File "Download selected".
   * Shown as bar buttons so operators don't have to open cmdk for a one-shot action.
   *
   * An explicit primary policy narrows the bar to primary commands; false-only opts into an Actions-only
   * bar. Entities that leave the policy unspecified retain their existing immediate buttons.
   */
  protected readonly $directCommands = computed(() => {

    const immediate = this.$commands().filter(command => !commandNeedsPalette(command));
    const primary = immediate.filter(isPrimaryCommand);
    const explicitPrimaryPolicy = this.$commands().some(command => typeof resolveDescriptor(command)?.primary === 'boolean');
    return explicitPrimaryPolicy ? primary : immediate;
  });
  /** Field-update / editor commands still go through the Actions palette. */
  protected readonly $paletteCommands = computed(() => this.$commands().filter(command => commandNeedsPalette(command)));
  protected readonly $showActionsPalette = computed(() => this.$commands().length > 0);

  public constructor() {

    DesignSystem.Mount(this.document, boardBulkActionsStyleId, boardBulkActionsBridgeStyles);
  }

  protected onActions(): void {

    this.command.emit({ action: actionsCommandId, data: this.$items() });
  }

  protected onDirectCommand(command: Command): void {

    const action = String(command?._id ?? '');
    if (!action) return;
    this.command.emit({ action, data: this.$items() });
  }
}

function commandNeedsPalette(command: Command): boolean {

  const descriptor = resolveDescriptor(command);
  if (!descriptor) return false;
  // Field updates and anything with an editor need the settings panel in cmdk.
  return descriptor.kind === 'field-update' || descriptor.editor != null;
}

function isPrimaryCommand(command: Command): boolean {

  return resolveDescriptor(command)?.primary === true;
}

function resolveDescriptor(command: Command): { kind?: string; editor?: unknown; primary?: boolean } | undefined {

  return (command.data as { bulkActionDescriptor?: { kind?: string; editor?: unknown; primary?: boolean } } | undefined)?.bulkActionDescriptor;
}
